import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import { useAudioRecorder } from "react-audio-voice-recorder";

const VoiceRecordingModal = ({ open, onClose, onSubmit, patientName }) => {
  const {
    startRecording,
    stopRecording,
    recordingBlob,
    isRecording,
    recordingTime,
  } = useAudioRecorder();

  const [audioUrl, setAudioUrl] = useState(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setAudioUrl(null);
    }
  }, [open]);

  useEffect(() => {
    if (recordingBlob) {
      const url = URL.createObjectURL(recordingBlob);
      setAudioUrl(url);
    }
  }, [recordingBlob]);

  const handleStop = () => {
    stopRecording();
  };

  const handleRetry = () => {
    setAudioUrl(null);
    startRecording();
  };

  const handleSubmit = () => {
    if (recordingBlob) {
      onSubmit(recordingBlob);
      handleClose();
    }
  };

  const handleClose = () => {
    // stop if recording?
    if (isRecording) stopRecording();
    setAudioUrl(null);
    onClose();
  };

  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min < 10 ? "0" + min : min}:${sec < 10 ? "0" + sec : sec}`;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: "#055f75ff", // Blue header
          padding: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "white",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <MicIcon />
          <Typography variant="h6" sx={{ fontFamily: "Lato", fontWeight: 600 }}>
            Voice Recording
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "300px",
          gap: 2,
          padding: 4,
        }}
      >
        {!isRecording && !audioUrl && (
          <>
            <MicIcon sx={{ fontSize: 60, color: "#6b7280" }} />
            <Typography
              variant="h6"
              sx={{ fontFamily: "Lato", fontWeight: 600, color: "#374151" }}
            >
              Ready to Record
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: "Lato", color: "#6b7280" }}
            >
              Tap start to begin voice recording
            </Typography>
          </>
        )}

        {isRecording && (
          <>
            <Box
              sx={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                backgroundColor: "#ef4444", // Red
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 0 10px rgba(239, 68, 68, 0.2)",
                animation: "pulse 1.5s infinite",
                "@keyframes pulse": {
                  "0%": {
                    boxShadow: "0 0 0 0 rgba(239, 68, 68, 0.7)",
                  },
                  "70%": {
                    boxShadow: "0 0 0 15px rgba(239, 68, 68, 0)",
                  },
                  "100%": {
                    boxShadow: "0 0 0 0 rgba(239, 68, 68, 0)",
                  },
                },
              }}
            >
              <MicIcon sx={{ fontSize: 40, color: "white" }} />
            </Box>
            <Typography
              variant="h6"
              sx={{ color: "#ef4444", fontFamily: "Lato", fontWeight: 600 }}
            >
              Recording...
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontFamily: "Lato", color: "#666" }}
            >
              {formatTime(recordingTime)}
            </Typography>
          </>
        )}

        {!isRecording && audioUrl && (
          <>
            <CheckCircleIcon sx={{ fontSize: 60, color: "#22c55e" }} />
            <Typography
              variant="h6"
              sx={{ color: "#22c55e", fontFamily: "Lato", fontWeight: 600 }}
            >
              Recording Completed
            </Typography>
            <Box sx={{ width: "100%", mt: 2 }}>
              <audio src={audioUrl} controls style={{ width: "100%" }} />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: 3, justifyContent: "end", gap: 1 , flexWrap:"wrap-reverse"}}>
        {!isRecording && !audioUrl && (
          <>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                backgroundColor: "#6b7280",
                color: "white",
                "&:hover": { backgroundColor: "#4b5563" },
                fontFamily: "Lato",
                textTransform: "none",
                px: 3,
                borderRadius:"8px"
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={startRecording}
              sx={{
                backgroundColor: "#ef4444",
                color: "white",
                "&:hover": { backgroundColor: "#dc2626" },
                fontFamily: "Lato",
                textTransform: "none",
                px: 3,
                 borderRadius:"8px"
              }}
              startIcon={<MicIcon />}
            >
              Start Recording
            </Button>
          </>
        )}

        {isRecording && (
          <Button
            variant="contained"
            onClick={handleStop}
            sx={{
              backgroundColor: "#ef4444",
              color: "white",
              "&:hover": { backgroundColor: "#dc2626" },
              fontFamily: "Lato",
              textTransform: "none",
              px: 4,
               borderRadius:"8px"
            }}
            startIcon={<StopIcon />}
          >
            Stop Recording
          </Button>
        )}

        {!isRecording && audioUrl && (
          <>
            <Button
              variant="contained"
              onClick={handleRetry}
              sx={{
                backgroundColor: "#6b7280",
                color: "white",
                "&:hover": { backgroundColor: "#4b5563" },
                fontFamily: "Lato",
                textTransform: "none",
                 borderRadius:"8px"
              }}
              startIcon={<ReplayIcon />}
            >
              Retry
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                backgroundColor: "#84cc16", // Lime green
                color: "white",
                "&:hover": { backgroundColor: "#65a30d" },
                fontFamily: "Lato",
                textTransform: "none",
                px: 4,
                 borderRadius:"8px"
              }}
            >
              Submit
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VoiceRecordingModal;
